// DB02 (option B): with the event's Auto Rejection on, an approved unpaid booking is
// auto-rejected when its ticket wave ends, and it can never be paid at an ended
// wave's price. Same wave-end rule as the Lambdas (scheduler / payment guard):
// a date-only endDate ("2026-05-20") is the end of that day in Cairo time and
// endDate <= startDate counts as "no end date" (untouched form defaults).

export const WAVE_ENDED_APPROVAL_MESSAGE =
  "This booking's ticket wave has ended; change its ticket/wave before approving.";

// Wave end of day in Cairo time (Africa/Cairo, DST-aware). Keep identical in:
// ticketingPaymentAutoShift workflowRules.js, ticketingEventsOperations index.js,
// ticketingPaymentOperations dbServices.js, admin src/helpers/waveDeadline.ts,
// web PaymentPage.tsx.
const CAIRO_TIME_ZONE = "Africa/Cairo";
const CAIRO_FALLBACK_OFFSET_MS = 2 * 60 * 60 * 1000;
// Offset (ms) of Cairo local time from UTC at instant `ts` (+2h, +3h in summer).
const getCairoOffsetMs = (ts: number): number => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: CAIRO_TIME_ZONE,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).formatToParts(new Date(ts));
    const get = (type: string) => Number((parts.find((p) => p.type === type) || ({} as any)).value);
    const asUtc = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour") % 24, get("minute"), get("second"));
    const offset = asUtc - Math.floor(ts / 1000) * 1000;
    return Number.isNaN(offset) ? CAIRO_FALLBACK_OFFSET_MS : offset;
  } catch (e) {
    return CAIRO_FALLBACK_OFFSET_MS;
  }
};
/** Epoch ms of 23:59:59.999 Cairo time on a "YYYY-MM-DD" date. */
export const getCairoEndOfDayTs = (ymd: string): number => {
  const [y, m, d] = String(ymd).split("-").map(Number);
  const localTs = Date.UTC(y, m - 1, d, 23, 59, 59, 999);
  const guess = localTs - getCairoOffsetMs(localTs);
  return localTs - getCairoOffsetMs(guess);
};

/** Epoch ms at which the wave ends, or null when it has no usable end date. */
export const getWaveEndTs = (wave: any): number | null => {
  if (!wave || !wave.endDate) return null;
  const raw = String(wave.endDate);
  const endTs = /^\d{4}-\d{2}-\d{2}$/.test(raw)
    ? getCairoEndOfDayTs(raw)
    : new Date(raw).getTime();
  if (Number.isNaN(endTs)) return null;
  const startTs = wave.startDate ? new Date(wave.startDate).getTime() : NaN;
  if (!Number.isNaN(startTs) && endTs <= startTs) return null;
  return endTs;
};

/** The booking's wave (by waveId) among the given waves. */
export const findBookingWave = (waves: any[] | null | undefined, waveId?: string | null) =>
  waveId ? (waves || []).find((w: any) => w && w.id === waveId) || null : null;

/**
 * End (epoch ms) of the booking's wave when it limits the payment window, i.e. the
 * event is published, has Auto Rejection on and the wave has an end date; otherwise null.
 */
export const getAutoRejectWaveEndTs = (params: {
  published?: boolean | null;
  autoRejectionEnabled?: boolean | null;
  waves?: any[] | null;
  waveId?: string | null;
}): number | null => {
  // Wave-end rules only act on bookings of published events
  if (params.published !== true || params.autoRejectionEnabled !== true) return null;
  return getWaveEndTs(findBookingWave(params.waves, params.waveId));
};

/** Caps an ISO expiry to the wave end (when given). Missing expiry -> wave end. */
export const capExpiryToWaveEnd = (
  expiryIso: string | null | undefined,
  waveEndTs: number | null,
): string | null | undefined => {
  if (waveEndTs === null) return expiryIso;
  const expiryTs = expiryIso ? new Date(expiryIso).getTime() : NaN;
  if (Number.isNaN(expiryTs) || expiryTs > waveEndTs) {
    return new Date(waveEndTs).toISOString();
  }
  return expiryIso;
};
